<template>
  <SogPage title="Recherche-Mails an Zulassungsstellen">
    <SogCard title="">
      <SogCardActions :is-toolbar="true">
        <SogCardActionButton
          v-show="_selectedId"
          mdi-icon="mdi-information"
          tooltip="Details anzeigen"
          @click="onDetails"
        />
        <div style="width: 240px">
          <SogDropDownList
            v-model="_statusFilter"
            placeholder="Status"
            :data-source="_statusItems"
            :fields="_fields"
            popup-width="240px"
          />
        </div>
        <SogCardActionButton :kind="SogButtonKind.Back" />
      </SogCardActions>
      <SogCardContent>
        <GridComponent
          ref="_grid"
          :data-source="_dataManager"
          :allow-paging="true"
          :allow-sorting="true"
          width="auto"
          :allow-resizing="true"
          :show-column-menu="false"
          :allow-text-wrap="true"
          :page-settings="_pageSettings"
          :row-selected="onRowSelected"
          :row-deselected="onRowDeselected"
          :record-double-click="onRecordDoubleClick"
          :action-complete="onActionComplete"
          :data-bound="onDataBound"
          :action-failure="gridActionFailure"
        >
          <ColumnsDirective>
            <ColumnDirective
              field="createDate"
              header-text="Erstellt"
              :auto-fit="true"
              format="dd.MM.yyyy"
              type="dateTime"
            />
            <ColumnDirective
              field="officeName"
              header-text="Zulassungsstelle"
              :auto-fit="true"
            />
            <ColumnDirective
              field="mailTypeText"
              header-text="Art"
              :auto-fit="true"
            />
            <ColumnDirective
              field="unknownCount"
              header-text="Zulassungen"
              :auto-fit="true"
              :value-accessor="getCountText"
            />
            <ColumnDirective
              field="statusText"
              header-text="Status"
              :auto-fit="true"
            />
            <ColumnDirective
              field="subject"
              header-text="Betreff"
              :auto-fit="true"
            />
          </ColumnsDirective>
        </GridComponent>
      </SogCardContent>
    </SogCard>
  </SogPage>
</template>

<script setup lang="ts">
import {reactive, ref, watch} from "vue";
import {useRouter} from "vue-router";
import {storeToRefs} from "pinia";
import {useRegistrationInquiryStore} from "@/modules/RegistrationInquiry/store";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective
} from "@syncfusion/ej2-vue-grids";
import {DataManager} from "@syncfusion/ej2-data/src/manager";
import {Api, gridActionFailure} from "@/shared/services/api";
import {CustomAdaptor} from "@/shared/types/CustomAdaptor";
import SogPage from "@/shared/ui/SogPage.vue";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import SogDropDownList from "@/shared/ui/SogDropDownList.vue";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";

/* eslint-disable @typescript-eslint/no-explicit-any */

const _router = useRouter();
const _grid = ref<any>(null);
const _store = useRegistrationInquiryStore();
const {statusFilter: _statusFilter, selectedId: _selectedId} = storeToRefs(_store);

const _statusItems = reactive([
  {id: "open", value: "Offen (Entwurf, Freigegeben, Fehler)"},
  {id: "Draft", value: "Entwurf"},
  {id: "Approved", value: "Freigegeben"},
  {id: "Failed", value: "Fehlgeschlagen"},
  {id: "Sent", value: "Gesendet"},
  {id: "Discarded", value: "Verworfen"},
]);
const _fields = {text: "value", value: "id"};

// Status als Filter-Prädikat: das Backend liest es aus $filter
const _adaptor = new CustomAdaptor();
_adaptor.addPredicate("status", "equal", _statusFilter.value);

const _dataManager = new DataManager({
  url: Api.baseUrl + "/VehicleRegistrationInquiryList",
  adaptor: _adaptor,
  crossDomain: true,
});

const _pageSettings = {
  pageSize: _store.listPageSettings.pageSize,
  currentPage: _store.listPageSettings.currentPage,
  pageSizes: ["All", 10, 25, 50, 100]
};

// Seitenwechsel und Seitengröße merken
function onActionComplete(args: any) {
  if (args.requestType !== "paging") return;
  const t_pageSettings = _grid.value?.ej2Instances?.pageSettings;
  if (t_pageSettings?.currentPage) {
    _store.listPageSettings.currentPage = t_pageSettings.currentPage;
  }
  if ([10, 25, 50, 100].includes(t_pageSettings?.pageSize)) {
    _store.listPageSettings.pageSize = t_pageSettings.pageSize;
  }
}

// Nach dem Laden die zuletzt markierte Anfrage wieder markieren
function onDataBound() {
  if (!_selectedId.value || !_grid.value?.ej2Instances) return;
  const t_records: any[] = _grid.value.ej2Instances.getCurrentViewRecords();
  const t_index = t_records.findIndex(t => t.id === _selectedId.value);
  if (t_index >= 0) {
    _grid.value.ej2Instances.selectRow(t_index);
  } else {
    _selectedId.value = undefined;
  }
}

function getCountText(field: string, data: any) {
  const t_all = data.itemCount ?? 0;
  const t_unknown = data.unknownCount ?? 0;
  return t_all > t_unknown ? `${t_unknown} (+${t_all - t_unknown})` : `${t_unknown}`;
}

function onRowSelected(args: any) {
  _selectedId.value = args.data?.id;
}

function onRowDeselected(args: any) {
  if (!args.isInteracted) return;
  _selectedId.value = undefined;
}

function onRecordDoubleClick(args: any) {
  if (args.rowData?.id) openDetails(args.rowData.id);
}

function onDetails() {
  if (_selectedId.value) openDetails(_selectedId.value);
}

function openDetails(id: string) {
  _router.push("/registrationinquiry/details/" + id);
}

// Statuswechsel: zurück auf Seite 1 und neu laden
watch(_statusFilter, (value) => {
  _adaptor.removePredicate("status");
  _adaptor.addPredicate("status", "equal", value);
  if (_grid.value?.ej2Instances?.pageSettings) {
    _grid.value.ej2Instances.pageSettings.currentPage = 1;
  }
  _grid.value?.refresh();
});
</script>
